import React, { useEffect, useRef, useMemo } from 'react';
import { observer } from 'mobx-react';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import { InputTextarea } from 'primereact/inputtextarea';
import { SectionHeader } from '../shared/SectionHeader';
import { FormField } from '../shared/FormField';
import { createHandlingTypeOptions } from '../../utils/gsmsHandlingTypeUtils';
import { useStore } from 'hooks/useStore';
import classes from '../../bidAnalysisTemplate.module.scss';

interface ProjectInformationSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
  templateData?: any;
  formData: any;
  analysisType: string;
  gsmsHndlgType: string;
  supplierName: string;
  supplierOptions: Array<{ label: string; value: string }>;
  contractsList: Array<{ id: number; contract_name: string }>;
  handlingTypesList: Array<{ id: number; handling_type: string }>;
  isIndirect: boolean;
  pricingValidatedChecked: boolean;
  validationErrors?: Record<string, string>;
  onFieldChange: (field: string, value: any) => void;
  onFieldBlur: (field: string, value: any) => void;
  onAnalysisTypeChange: (value: string) => void;
  onGsmsHndlgTypeChange: (value: string) => void;
  onSupplierChange: (value: string) => void;
  onContractsChange: (selected: number[], contractsData: any[]) => void;
  onContractsFilter?: () => void;
  isSaving?: boolean;
  notesCount?: number;
  onNotesClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isManagerReviewMode?: boolean; // Show notes button only for managers in review mode
  templateType?: string; // 'mas' | 'nc' - to distinguish between MAS and NC templates
}

export const ProjectInformationSection: React.FC<ProjectInformationSectionProps> = observer(({
  isExpanded,
  onToggle,
  templateData,
  formData,
  analysisType,
  gsmsHndlgType,
  supplierName,
  supplierOptions,
  contractsList,
  handlingTypesList,
  isIndirect,
  pricingValidatedChecked,
  validationErrors = {},
  onFieldChange,
  onFieldBlur,
  onAnalysisTypeChange,
  onGsmsHndlgTypeChange,
  onSupplierChange,
  onContractsChange,
  onContractsFilter,
  isSaving = false,
  notesCount,
  onNotesClick,
  isManagerReviewMode = false,
  templateType,
}) => {
  const { BidAnalysisStore } = useStore();
  const bidDetails = BidAnalysisStore.currentBidDetails;
  const isNC = templateType?.toLowerCase() === 'nc';
  const hasSetDefaultAnalysisDate = useRef(false);

  // Resolve Analysis Date for display: use formData.analysisDate if valid, else default to today so Calendar always shows a date
  const analysisDateValue = useMemo(() => {
    const raw = formData.analysisDate;
    if (raw instanceof Date && !isNaN(raw.getTime())) {
      return raw;
    }
    if (raw) {
      try {
        const parsed = typeof raw === 'string' ? new Date(raw) : new Date(raw);
        if (!isNaN(parsed.getTime())) {
          return parsed;
        }
      } catch (_) {
        // ignore
      }
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, [formData.analysisDate]);

  // When Analysis Date is missing in formData, persist default (today) to parent once so it shows and is saved
  useEffect(() => {
    if (hasSetDefaultAnalysisDate.current) return;
    const raw = formData.analysisDate;
    const hasValid = raw instanceof Date ? !isNaN((raw as Date).getTime()) : raw && !isNaN(new Date(raw).getTime());
    if (!hasValid) {
      hasSetDefaultAnalysisDate.current = true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      onFieldChange('analysisDate', today);
    }
  }, [formData.analysisDate, onFieldChange]);

  // Contract types for NC template
  const ncContractTypes = [
    { label: 'VA NC', value: 'VA NC' },
    { label: 'DLA NC', value: 'DLA NC' },
    { label: 'National Contract', value: 'National Contract' },
    { label: 'VA Recompete', value: 'VA Recompete' },
    { label: 'DLA Recompete', value: 'DLA Recompete' },
    { label: 'Reverse Action Floor', value: 'Reverse Action Floor' },
    { label: 'Reverse Auction Starting Bid', value: 'Reverse Auction Starting Bid' },
    { label: 'FPR', value: 'FPR' },
  ];
 

  return (
    <div className={classes.section}>
      <SectionHeader
        title="Project Information"
        isExpanded={isExpanded}
        onToggle={onToggle}
        notesCount={notesCount}
        onNotesClick={onNotesClick}
        sectionName="project-information"
        isManagerReviewMode={isManagerReviewMode}
      />
      {isExpanded && (
        <div className={classes.sectionContent}>
          <div className={classes.formGrid4}>
            <FormField label="Product Name" required>
              <InputText
                value={bidDetails?.product_name || templateData?.projectInformation?.productName || ''}
                disabled
              />
            </FormField>

            <FormField label="Analyst Name">
              <InputText
                value={bidDetails?.analyst || templateData?.projectInformation?.analystName || ''}
                disabled
              />
            </FormField>

            <FormField label="Analysis Date">
              <Calendar
                value={analysisDateValue}
                dateFormat="mm/dd/yy"
                showIcon
                onChange={(e) => {
                  if (e.value) {
                    onFieldChange('analysisDate', e.value);
                    onFieldBlur('analysisDate', e.value);
                  } else {
                    onFieldChange('analysisDate', null);
                    onFieldBlur('analysisDate', null);
                  }
                }}
              />
            </FormField>

            <FormField label="Target Launch Date" required error={validationErrors.targetLaunchDate}>
              <Calendar
                value={formData.targetLaunchDate ? new Date(formData.targetLaunchDate) : (templateData?.projectInformation?.targetLaunchDate ? new Date(templateData.projectInformation.targetLaunchDate) : new Date())}
                dateFormat="mm/dd/yy"
                showIcon
                className={validationErrors.targetLaunchDate ? 'p-invalid' : ''}
                minDate={analysisDateValue || undefined}
                onChange={(e) => {
                  if (e.value) {
                    onFieldChange('targetLaunchDate', e.value);
                    onFieldBlur('targetLaunchDate', e.value);
                  }
                }}
              />
            </FormField>

            <FormField label="Analysis Type" required>
              <Dropdown
                value={analysisType}
                options={[
                  { label: 'New Launch', value: 'New Launch' },
                  { label: 'Price Change', value: 'Price Change' }
                ]}
                onChange={(e) => {
                  onAnalysisTypeChange(e.value);
                  onFieldBlur('analysisType', e.value);
                }}
              />
            </FormField>

            <FormField label="GSMS Handling Type" required>
              <Dropdown
                value={gsmsHndlgType || formData.gsmsHndlgType || ''}
                options={isIndirect
                  ? [{ label: 'Marketing Income - MI', value: 'Marketing Income' }]
                  : createHandlingTypeOptions(handlingTypesList)
                }
                disabled={isIndirect || handlingTypesList.length === 0}
                onChange={(e) => {
                  const value = e.value;
                  onGsmsHndlgTypeChange(value);
                  onFieldChange('gsmsHndlgType', value);
                  if (!isIndirect) {
                    onFieldBlur('gsmsHndlgType', value);
                  }
                }}
                placeholder={isIndirect ? 'Marketing Income - MI' : (handlingTypesList.length === 0 ? "Loading..." : "Select Handling Type")}
                filter={!isIndirect}
              />
            </FormField>

            <FormField label="Supplier Name" required>
              <Dropdown
                value={supplierName}
                options={supplierOptions.length > 0 ? supplierOptions : [{ label: supplierName || 'Select Supplier', value: supplierName || '' }]}
                onChange={(e) => {
                  onSupplierChange(e.value);
                  onFieldChange('supplierName', e.value);
                  onFieldBlur('supplierName', e.value);
                }}
                placeholder="Select Supplier"
                filter
              />
            </FormField>

            <FormField label="Contract(s)" required>
              {isNC ? (
                <MultiSelect
                  value={(() => {
                    // If contracts is a string (comma-separated), convert to array of values
                    if (typeof formData.contracts === 'string' && formData.contracts.trim()) {
                      const contractNames = formData.contracts.split(',').map((name: string) => name.trim());
                      return contractNames.filter((name: string) => ncContractTypes.some(ct => ct.value === name));
                    }
                    // If contracts is already an array, use it
                    if (Array.isArray(formData.contracts)) {
                      return formData.contracts;
                    }
                    return [];
                  })()}
                  options={ncContractTypes}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => {
                    const selected = e.value === null ? [] : (Array.isArray(e.value) ? e.value : [e.value]);
                    const selectedString = selected.length > 0 ? selected.join(', ') : '';
                    // Use contracts field instead of contractType
                    onFieldChange('contracts', selectedString);
                    onFieldBlur('contracts', selectedString);
                    // Also update contractsData for consistency
                    const selectedContractsData = ncContractTypes.filter(ct => selected.includes(ct.value));
                    onContractsChange(selected.map((v: string) => ncContractTypes.findIndex(ct => ct.value === v)), selectedContractsData);
                  }}
                  placeholder="Select Contract(s)"
                  filter
                  showClear
                  display="chip"
                  maxSelectedLabels={3}
                  selectedItemsLabel="{0} contracts selected"
                />
              ) : (
                <MultiSelect
                  value={(() => {
                    // If contractsData exists, use it to get IDs
                    if (formData.contractsData && Array.isArray(formData.contractsData)) {
                      return formData.contractsData.map((c: any) => c.id);
                    }
                    // If contracts is a string (comma-separated names), convert to IDs
                    if (typeof formData.contracts === 'string' && formData.contracts.trim()) {
                      const contractNames = formData.contracts.split(',').map((name: string) => name.trim());
                      return contractsList
                        .filter(c => contractNames.includes(c.contract_name))
                        .map(c => c.id);
                    }
                    // If contracts is already an array of IDs, use it
                    if (Array.isArray(formData.contracts)) {
                      return formData.contracts;
                    }
                    // Default to empty array
                    return [];
                  })()}
                  options={contractsList}
                  optionLabel="contract_name"
                  optionValue="id"
                  onChange={(e) => {
                    const selected: number[] = e.value === null ? [] : (Array.isArray(e.value) ? e.value : [e.value]);
                    const selectedContractsData = contractsList.filter(c => selected.includes(c.id));
                    onContractsChange(selected, selectedContractsData);
                  }}
                  onHide={() => {
                    // When dropdown closes (by clicking outside or pressing escape), trigger filter
                    if (onContractsFilter && !isSaving) {
                      onContractsFilter();
                    }
                  }}
                  placeholder={contractsList.length === 0 ? "Loading contracts..." : "Select Contract(s)"}
                  filter
                  showClear
                  disabled={contractsList.length === 0}
                  display="chip"
                  maxSelectedLabels={3}
                  selectedItemsLabel="{0} contracts selected"
                />
              )}
              {!isNC && contractsList.length === 0 && (
                <span style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                  No contracts available
                </span>
              )}
            </FormField>
          </div>

          <div className={classes.approvalSection}>
            <h4>Approvals Tracking</h4>
            <div className={classes.approvalGrid}>
              <div className={classes.checkboxField}>
                <Checkbox
                  inputId="pricingValidated"
                  checked={pricingValidatedChecked}
                  disabled
                />
                <label htmlFor="pricingValidated">Pricing Validated by P&C</label>
                <span className={classes.pendingLabel}>Pending</span>
              </div>
              <FormField label="Effective Date">
                <Calendar
                  placeholder="mm/dd/yyyy"
                  showIcon
                  disabled
                  onChange={(e) => {
                    if (e.value) onFieldBlur('effectiveDate', e.value);
                  }}
                />
              </FormField>
              <FormField label="Approval Notes">
                <InputTextarea
                  placeholder="Enter approval notes..."
                  rows={3}
                  disabled
                  onBlur={(e) => onFieldBlur('approvalNotes', e.target.value)}
                />
              </FormField>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

